/**
 * About Content Section
 * Main content with image and text - fetched from CMS API
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const CMS_API_URL = process.env.REACT_APP_API_BASE_URL_CMS || '';
const ABOUT_INFO_ID = 16;

const AboutContent = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Fetch content from CMS API
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.post(
          `${CMS_API_URL}info/get`,
          { InfoId: ABOUT_INFO_ID },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000,
          }
        );

        if (response.data?.code === 1 && Array.isArray(response.data?.data) && response.data.data.length > 0) {
          const data = response.data.data[0];
          // Only use if not deleted and published
          if (data.IsDeleted === false && data.IsPublished === true) {
            setContent({
              Title: data.Title,
              Description: data.Description,
              Image: data.Image,
            });
          }
        }
      } catch (error) {
        console.error('Failed to fetch about content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Carousel for multiple images (use API image if available)
  const carouselImages = content?.Image
    ? [{ src: content.Image, alt: content.Title }]
    : [];

  const SLIDE_INTERVAL = 4000;

  const nextSlide = useCallback(() => {
    if (carouselImages.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  }, [carouselImages.length]);

  useEffect(() => {
    if (carouselImages.length <= 1) return;
    const timer = setInterval(nextSlide, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [nextSlide, carouselImages.length]);

  // Show loading skeleton while fetching data
  if (loading) {
    return (
      <section className="pt-7 pb-0 bg-white">
        <div className="container mx-auto px-6 md:px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <div className="h-10 w-48 bg-gray-200 rounded mb-6 animate-pulse" />
              <div className="w-16 h-1 bg-gray-200 mb-6 animate-pulse" />
              <div className="space-y-4">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
            <div className="h-64 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </section>
    );
  }

  // Return null if no data from API after loading
  if (!content) {
    return null;
  }

  return (
    <section className="pt-7 pb-0 bg-white">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        {/* <div className="grid md:grid-cols-2 gap-6 items-start"> */}
        <div className="grid md:grid-cols-[1.4fr_0.9fr] gap-6 items-start">
          {/* Left Image */}
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#2f3951] mb-6">
              {content.Title}
            </h2>
            <div className="w-16 h-1 bg-[#ca9a37] mb-6"></div>

            {/* Preview text — first ~11 lines */}
            <div className="relative text-gray-600 leading-relaxed text-left w-full space-y-4 pr-4 text-justify max-h-[270px] overflow-hidden">
              {(content.Description || '')
                .replace(/<[^>]*>/g, '\n')
                .replace(/&nbsp;/g, ' ')
                .replace(/&amp;/g, '&')
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .split('\n')
                .filter(p => p.trim().length > 0)
                .map((paragraph, index) => (
                  <p key={index} className="text-gray-600 leading-relaxed">
                    {paragraph.trim()}
                  </p>
                ))}
              <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            </div>

            <Link
              to="/about/who-are-we"
              className="inline-flex items-center mt-6 bg-[#ca9a37] hover:bg-[#b88a2f] text-white font-semibold px-6 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-[#ca9a37] focus:ring-offset-2"
            >
              Read More
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

          {/* Right Image with gold background */}
          {/* <div className="relative flex justify-center md:justify-end mt-5"> */}
          <div className="relative flex justify-center md:justify-center mt-5">
            <div className="relative">
              {/* Solid gold background block */}
              <div className="absolute -top-7 -left-7 w-[65%] h-[113%] bg-[#ca9a37] rounded-sm"></div>
              {/* Carousel */}
              <div className="relative rounded-sm overflow-hidden shadow-xl bg-white w-full max-w-sm">
                {carouselImages.map((image, index) => (
                  <div
                    key={index}
                    className={`transition-opacity duration-700 ease-in-out ${
                      index === currentSlide ? 'opacity-100 relative' : 'opacity-0 absolute inset-0'
                    }`}
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-auto object-cover"
                    />
                  </div>
                ))}
                {/* Dot indicators - only show if more than 1 image */}
                {carouselImages.length > 1 && (
                  <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
                    {carouselImages.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setCurrentSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all focus:outline-none ${
                          index === currentSlide ? 'bg-[#ca9a37] w-4' : 'bg-white/70 hover:bg-white'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutContent;
